/**
 * Fotos aufbereiten, bevor sie in die Warteschlange gehen.
 *
 * Ein Handyfoto ist heute 4000 px breit und vier Megabyte gross. Für die
 * Dokumentation einer Baustelle ist das Verschwendung in jeder Richtung: es
 * verstopft die Leitung im Funkloch, füllt den Speicher des Geräts und macht
 * später jede Rechnung langsam. 1600 px an der langen Kante zeigen einen
 * Wasserschaden genauso deutlich und wiegen ein Zehntel.
 *
 * Zwei Fallen stecken in diesem scheinbar simplen Vorgang:
 *
 * 1. EXIF-Drehung. Ein Handy speichert das Bild oft in Sensorlage und legt die
 *    Drehung als Zahl daneben. Wer nur skaliert, bekommt liegende Fotos – und
 *    zwar genau bei denen, die im Hochformat aufgenommen wurden, also bei
 *    fast allen.
 *
 * 2. Der Zeitpunkt. Verkleinert wird SOFORT beim Aufnehmen, nicht beim
 *    Hochladen. Sonst liegen zehn Vier-Megabyte-Bilder im Gerätespeicher, und
 *    Safari räumt sie irgendwann weg.
 */

export const MAX_EDGE_PX = 1600
export const JPEG_QUALITY = 0.8

export interface PreparedImage {
  blob: Blob
  width: number
  height: number
  /** Ursprungsgrösse, damit der Bericht zeigen kann, was eingespart wurde. */
  sourceBytes: number
  rotated: boolean
}

/**
 * EXIF-Orientierung aus einem JPEG lesen.
 *
 * Bewusst von Hand und ohne Fremdpaket: gebraucht wird genau ein Feld, und
 * eine EXIF-Bibliothek wäre für diese eine Zahl 40 KB im Startpaket der App –
 * das lädt jeder Monteur bei jedem Besuch mit.
 *
 * Gelesen wird nur der Anfang der Datei. Findet sich nichts, gilt 1 (keine
 * Drehung), und der Rest funktioniert trotzdem.
 */
export function readExifOrientation(buffer: ArrayBuffer): number {
  const view = new DataView(buffer)
  if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) return 1 // kein JPEG

  let offset = 2
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset, false)
    offset += 2
    if (marker === 0xffe1) {
      // APP1 – hier steckt EXIF
      if (offset + 8 > view.byteLength) return 1
      if (view.getUint32(offset + 2, false) !== 0x45786966) return 1 // "Exif"
      const tiff = offset + 8
      if (tiff + 8 > view.byteLength) return 1
      const little = view.getUint16(tiff, false) === 0x4949
      const dirStart = tiff + view.getUint32(tiff + 4, little)
      if (dirStart + 2 > view.byteLength) return 1
      const entries = view.getUint16(dirStart, little)
      for (let i = 0; i < entries; i++) {
        const entry = dirStart + 2 + i * 12
        if (entry + 12 > view.byteLength) return 1
        if (view.getUint16(entry, little) === 0x0112) {
          return view.getUint16(entry + 8, little) || 1
        }
      }
      return 1
    }
    if ((marker & 0xff00) !== 0xff00) return 1
    if (offset + 2 > view.byteLength) return 1
    offset += view.getUint16(offset, false)
  }
  return 1
}

/** Vertauscht die Orientierung Breite und Höhe? */
export function swapsAxes(orientation: number): boolean {
  return orientation >= 5 && orientation <= 8
}

/**
 * Die acht EXIF-Orientierungen als Zeichenoperation.
 *
 * Die Fälle 2, 4, 5 und 7 sind gespiegelt. Sie kommen selten vor, kosten aber
 * nur je eine Zeile – und wenn sie fehlen, ist das Foto seitenverkehrt, was
 * niemand sofort bemerkt und niemand mehr korrigieren kann.
 */
export function applyOrientation(
  ctx: CanvasRenderingContext2D,
  orientation: number,
  width: number,
  height: number
): void {
  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, width, 0)
      break
    case 3:
      ctx.transform(-1, 0, 0, -1, width, height)
      break
    case 4:
      ctx.transform(1, 0, 0, -1, 0, height)
      break
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0)
      break
    case 6:
      ctx.transform(0, 1, -1, 0, height, 0)
      break
    case 7:
      ctx.transform(0, -1, -1, 0, height, width)
      break
    case 8:
      ctx.transform(0, -1, 1, 0, 0, width)
      break
    default:
      break
  }
}

/** Zielmasse bei gegebener Quellgrösse – Seitenverhältnis bleibt. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge = MAX_EDGE_PX
): { width: number; height: number } {
  const lang = Math.max(width, height)
  if (lang <= maxEdge) return { width, height }
  const faktor = maxEdge / lang
  return { width: Math.round(width * faktor), height: Math.round(height * faktor) }
}

/**
 * Aus einer Datei ein aufbereitetes JPEG machen.
 *
 * Läuft nur im Browser – im Test wird die reine Rechnerei oben geprüft, nicht
 * das Zeichnen.
 */
export async function prepareImage(file: Blob, maxEdge = MAX_EDGE_PX): Promise<PreparedImage> {
  const sourceBytes = file.size
  const buffer = await file.arrayBuffer()
  const orientation = readExifOrientation(buffer)

  const bitmap = await createImageBitmap(file)
  const roh = swapsAxes(orientation)
    ? { width: bitmap.height, height: bitmap.width }
    : { width: bitmap.width, height: bitmap.height }
  const ziel = fitWithin(roh.width, roh.height, maxEdge)

  const canvas = document.createElement('canvas')
  canvas.width = ziel.width
  canvas.height = ziel.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Das Gerät kann das Bild nicht verkleinern.')

  applyOrientation(ctx, orientation, ziel.width, ziel.height)
  // Nach der Drehung zeichnet man in das ungedrehte Rechteck.
  if (swapsAxes(orientation)) {
    ctx.drawImage(bitmap, 0, 0, ziel.height, ziel.width)
  } else {
    ctx.drawImage(bitmap, 0, 0, ziel.width, ziel.height)
  }
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  )
  if (!blob) throw new Error('Das Bild liess sich nicht speichern.')

  return {
    blob,
    width: ziel.width,
    height: ziel.height,
    sourceBytes,
    rotated: orientation !== 1
  }
}

/** Blob → Base64 ohne Präfix, für den Upload über den apiClient. */
export async function toBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  // In Blöcken, sonst sprengt ein grosses Bild den Aufrufstapel.
  const block = 0x8000
  for (let i = 0; i < bytes.length; i += block) {
    binary += String.fromCharCode(...bytes.subarray(i, i + block))
  }
  return btoa(binary)
}
